import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { AUDIT_EVENTS_COLLECTION, firestore } from "../lib/firebase";
import { asRecord, toDate } from "../utils/firestoreData";

export type AuditMetadata = Record<string, string | number | boolean | null>;

export type AuditEventRecord = {
  actorUid: string | null;
  correlationId: string | null;
  createdAt: Date | null;
  eventType: string;
  id: string;
  metadata: AuditMetadata;
};

export type AuditEventInput = {
  actorUid: string | null;
  correlationId?: string | null;
  eventType: string;
  metadata?: AuditMetadata;
};

function normalizeMetadata(value: unknown): AuditMetadata {
  const data = asRecord(value);
  const entries = Object.entries(data).filter(
    (entry): entry is [string, string | number | boolean | null] => {
      const [, nextValue] = entry;
      return (
        nextValue === null ||
        typeof nextValue === "string" ||
        typeof nextValue === "number" ||
        typeof nextValue === "boolean"
      );
    },
  );

  return Object.fromEntries(entries);
}

export async function writeAuditEvent({
  actorUid,
  correlationId = null,
  eventType,
  metadata = {},
}: AuditEventInput): Promise<void> {
  if (!firestore) {
    return;
  }

  await addDoc(collection(firestore, AUDIT_EVENTS_COLLECTION), {
    actorUid,
    correlationId,
    eventType,
    metadata,
    createdAt: serverTimestamp(),
  });
}

export async function listAuditEvents(
  maxResults = 80,
): Promise<AuditEventRecord[]> {
  if (!firestore) {
    return [];
  }

  const auditQuery = query(
    collection(firestore, AUDIT_EVENTS_COLLECTION),
    orderBy("createdAt", "desc"),
    limit(maxResults),
  );
  const snapshot = await getDocs(auditQuery);

  return snapshot.docs.map((auditDoc) => {
    const data = asRecord(auditDoc.data());

    return {
      actorUid: typeof data.actorUid === "string" ? data.actorUid : null,
      correlationId:
        typeof data.correlationId === "string" ? data.correlationId : null,
      createdAt: toDate(data.createdAt),
      eventType:
        typeof data.eventType === "string" ? data.eventType : "audit.unknown",
      id: auditDoc.id,
      metadata: normalizeMetadata(data.metadata),
    };
  });
}
